'use client';
/**
 * LA SIGNATURE D'UNE PERSONNE — image PNG et texte, dans son dossier RH (RQ, 11/10/2026).
 *
 * Elle ne s'appose qu'à une validation biologique (MoMent) : le CR validé en
 * RECOPIE l'image et le texte, avec la date et l'heure. La changer ici ne
 * modifie aucun document déjà signé. Un document préparé sans validation
 * (technicien) n'en porte aucune.
 *
 * Seul un biologiste (fonction `f_biologiste`) en a une : pour les autres,
 * l'encart ne s'affiche pas — le serveur le dit et refuse tout dépôt (RQ, 03/11/2026).
 *
 * Modifiable par la personne elle-même, ou par le RQ / le RH — le serveur
 * revérifie ; le journal dit qui.
 */
import React, { useEffect, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { fmtDateTime } from '@/lib/meta';
import { card, inputStyle, FieldError, useBusy } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';

type Etat = { biologiste?: boolean; dataUrl: string | null; texte: string; version: number | null; deposeLe: string | null; historique: { quoi: string; version: number | null; le: string; par: string }[] };

const versBase64 = (f: File) => new Promise<string>((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result || '').split(',')[1] || '');
  fr.onerror = () => reject(new Error('lecture du fichier impossible'));
  fr.readAsDataURL(f);
});

export function SignaturePersonne({ orgId, modifiable }: { orgId: string; modifiable: boolean }) {
  const { showToast } = useGed();
  const [etat, setEtat] = useState<Etat | null>(null);
  const [echec, setEchec] = useState('');
  const [texte, setTexte] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const fichier = useRef<HTMLInputElement>(null);
  const url = `/api/ged/ged/rh/personnel/${encodeURIComponent(orgId)}/signature`;

  useEffect(() => {
    let vivant = true;
    void fetch(url, { credentials: 'include' })
      .then(async (r) => { const j = await r.json().catch(() => null); if (!vivant) return; if (r.ok) setEtat(j); else setEchec(j?.error?.message || 'Signature non consultable.'); })
      .catch(() => { if (vivant) setEchec('Signature non consultable.'); });
    return () => { vivant = false; };
  }, [url]);

  const envoyer = (corps: Record<string, unknown>, message: string) => run('sig', async () => {
    setErreur('');
    const r = await fetchEcriture(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) });
    const j = await r.json().catch(() => null);
    if (!r.ok) { setErreur(j?.error?.message || 'Enregistrement refusé.'); return; }
    showToast(message);
    const relu = await fetch(url, { credentials: 'include' }).then((x) => (x.ok ? x.json() : null)).catch(() => null);
    if (relu) setEtat(relu);
    setTexte(null);
  });

  if (echec) return <section style={{ ...card, padding: 16, fontSize: 12.5, color: 'var(--text-muted)' }}><h3 style={{ margin: '0 0 6px', fontSize: 14 }}>Signature</h3>{echec}</section>;
  if (!etat || etat.biologiste === false) return null;
  return (
    <section style={{ ...card, padding: 16, display: 'grid', gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--text-strong)' }}>Signature</h3>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Apposée sur un compte rendu au moment de sa <strong>validation biologique</strong> (MoMento), avec la date et l’heure.
        Un document préparé sans validation n’en porte aucune.
      </p>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: 14, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
          <div style={{ width: '100%', minHeight: 70, border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)', display: 'grid', placeItems: 'center', background: 'var(--surface-card)' }}>
            {etat.dataUrl
              /* eslint-disable-next-line @next/next/no-img-element -- data-URI servie par le moteur, rien à optimiser */
              ? <img src={etat.dataUrl} alt="Signature" style={{ maxWidth: '100%', maxHeight: 70 }} />
              : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Aucune image</span>}
          </div>
          {etat.dataUrl && <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>v{etat.version} · déposée le {fmtDateTime(etat.deposeLe)}</span>}
          {modifiable && (<>
            <input ref={fichier} data-sans-allegement="Signature : PNG transparent, sa forme compte" type="file" accept="image/png" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void versBase64(f).then((b) => envoyer({ base64: b }, 'Image de signature déposée.')); e.target.value = ''; }} />
            <Bouton taille="sm" chargement={estOccupe('sig')} onClick={() => fichier.current?.click()}>{etat.dataUrl ? 'Remplacer l’image (PNG)' : 'Déposer l’image (PNG)'}</Bouton>
          </>)}
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          <label htmlFor={`sig-texte-${orgId}`} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>Texte de signature — une ligne par ligne imprimée</label>
          {texte === null ? (<>
            <div style={{ whiteSpace: 'pre-line', fontSize: 13, lineHeight: 1.45, color: etat.texte ? 'var(--text-strong)' : 'var(--text-muted)' }}>{etat.texte || 'Aucun texte : le CR imprimera seulement le nom.'}</div>
            {modifiable && <div><Bouton taille="sm" onClick={() => setTexte(etat.texte)}>Modifier le texte</Bouton></div>}
          </>) : (<>
            <textarea id={`sig-texte-${orgId}`} rows={4} value={texte} onChange={(e) => setTexte(e.target.value)} maxLength={400}
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }}
              placeholder={'Dr Prénom Nom\nBiologiste médicale\nSpécialités…'} />
            <PiedActions taille="sm" annuler={{ onClick: () => setTexte(null) }}
              principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('sig'), disabled: estOccupe(), onClick: () => void envoyer({ texte }, 'Texte de signature enregistré.') }} />
          </>)}
        </div>
      </div>
      <FieldError>{erreur}</FieldError>
    </section>
  );
}
